<template>
  <div class="surface-overlay shadow-3 sticky top-0 z-10">
    <div class="flex flex-row h-fit" role="navigation">
      <!-- Logo + Resumes / Change Review -->
      <router-link to="/" aria-label="home">
        <img
          alt="Plato logo"
          id="plato-logo"
          src="@/assets/PLATO-Logo.png"
          class="mr-0 lg:mr-6 h-1rem"
        />
      </router-link>

      <div class="my-3 flex flex-row gap-2 items-center">
        <router-link v-if="pagePerms.resumesPage" to="/" aria-label="home">
          <Button label="Resumes" aria-label="resumes" severity="secondary" text />
        </router-link>

        <router-link v-if="changeReviewEnabled && pagePerms.changeLogPage" to="/change-review">
          <Button label="Change Review" severity="secondary" text />
        </router-link>
      </div>

      <!-- Right side navigation -->
      <div class="ms-auto gap-2 my-3">
        <nav class="flex flex-row gap-2 items-center">
          <!-- Advanced Search -->
          <router-link v-if="pagePerms.advancedSearchPage" to="/search">
            <Button
              label="Advanced Search"
              aria-label="advanced search"
              severity="secondary"
              text
              :class="pagePerms.advancedSearchPage ? '' : 'hidden p-disabled'"
            />
          </router-link>
          <!-- Metrics -->
          <router-link v-if="pagePerms.metricsPage" to="/metrics">
            <Button label="Metrics" aria-label="metrics" severity="secondary" text />
          </router-link>

          <!-- Admin / Limited Admin menu -->
          <span v-if="isAdmin">
            <AdminNavMenu menu-label="Admin" />
          </span>
          <span v-else-if="!isEmployee && temporaryHasCheck">
            <AdminNavMenu menu-label="Menu" />
          </span>
          <router-link to="/issues">
            <Button
              label="Issues"
              aria-label="issues"
              severity="secondary"
              text
              :class="issuesEnabled ? '' : 'hidden p-disabled'"
            />
          </router-link>
          <!-- Help + User Menu -->
          <HelpNavMenu />
          <UserNavMenu />
        </nav>
      </div>
    </div>
  </div>

  <!-- Dev-only context display & role toggles -->
  <RoleSwitch v-if="!isProdEnvironment && isAuthenticated" />
</template>

<script setup lang="ts">
import { computed } from "vue";
import UserNavMenu from "./UserNavMenu.vue";
import AdminNavMenu from "./AdminNavMenu.vue";
import HelpNavMenu from "./HelpNavMenu.vue";
import { useAppContextStore } from "@/core/store";
import { useEnvironmentFlags } from "@/core/composables/useEnvironmentFlags";
import { usePermissions } from "@/core/composables/usePermissions";
import { RoleSwitch } from "@/shared/components";

const contextStore = useAppContextStore();
if (!contextStore) {
  throw new Error("TopNav: contextStore injection missing");
}

const context = computed(() => contextStore.context);

const { changeReviewEnabled, issuesEnabled, isProdEnvironment } = useEnvironmentFlags();
const { isAdmin, isEmployee, isAuthenticated } = usePermissions(context.value.user);

const pagePerms = computed<Record<string, boolean>>(() => {
  return ((context.value?.user as any)?.permissions?.page ?? {}) as Record<string, boolean>;
});

const temporaryHasCheck = computed(
  () =>
    !!(
      pagePerms.value.employeesPage ||
      pagePerms.value.managersPage ||
      pagePerms.value.resumeTrackerPage
    )
);
</script>

<style scoped>
#plato-logo {
  max-height: 2.5rem;
}
</style>
